import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Calendar, Gauge, ChevronLeft, ChevronRight, Send, Phone, Mail, Eye, Fuel, Users, X, Maximize2, ZoomIn, ZoomOut, User, Heart, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import { Textarea } from '../components/ui/textarea';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_IMAGES = {
  jet: '/images/categorie/jet.jpg',
  turboelica: '/images/categorie/turboelica.png',
  elicotteri: '/images/categorie/elicotteri.png',
  ultraleggeri: '/images/categorie/ultraleggeri.png',
  'propulsione-singola': '/images/categorie/propulsione-singola.jpg',
  'propulsione-multipla': '/images/categorie/propulsione-multipla.jpg',
  default: 'https://images.unsplash.com/photo-1644945082110-eaeb76f03129?w=1200&h=800&fit=crop',
};

const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#18181B' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#71717A' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#09090B' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#27272A' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#09090B' }] },
];

function formatPrice(p) {
  if (!p) return 'Su richiesta';
  return `€${Number(p).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
}

function ImageLightbox({ images, startIndex, onClose, title }) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const prev = () => { setIdx((idx - 1 + images.length) % images.length); setZoom(1); };
  const next = () => { setIdx((idx + 1) % images.length); setZoom(1); };
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="image-lightbox" onClick={onClose}>
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(0.5, z - 0.25)); }} className="w-10 h-10 bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059]" data-testid="lightbox-zoom-out">
          <ZoomOut size={16} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(3, z + 0.25)); }} className="w-10 h-10 bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059]" data-testid="lightbox-zoom-in">
          <ZoomIn size={16} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} className="w-10 h-10 bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059]" data-testid="lightbox-fullscreen">
          <Maximize2 size={16} />
        </button>
        <button onClick={onClose} className="w-10 h-10 bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-white hover:border-red-500" data-testid="lightbox-close">
          <X size={16} />
        </button>
      </div>

      <div className="absolute top-4 left-4 text-xs text-zinc-500 font-mono z-10">{idx + 1} / {images.length}</div>

      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/60 border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059] z-10">
            <ChevronLeft size={20} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-zinc-900/60 border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059] z-10">
            <ChevronRight size={20} />
          </button>
        </>
      )}

      <img
        src={images[idx]}
        alt={title ? `${title} — Foto ${idx + 1} di ${images.length} | AeroDeals` : `Foto ${idx + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-200"
        style={{ transform: `scale(${zoom})` }}
        onClick={e => e.stopPropagation()}
        draggable={false}
      />

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {images.map((img, i) => (
            <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); setZoom(1); }} className={`w-12 h-8 border overflow-hidden transition-all ${i === idx ? 'border-[#C5A059]' : 'border-zinc-700 opacity-50 hover:opacity-80'}`}>
              <img src={img} alt={title ? `${title} — miniatura ${i + 1}` : `Miniatura ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ListingDetail() {
  const params = useParams();
  // Route patterns:
  //   /listings/:id                     → params.id = 'listing_xxx'
  //   /listings/:category/:slug         → params.category + params.slug (combined slug = 'cat/slug')
  const isSlugRoute = !!params.slug;
  const fetchKey = isSlugRoute ? `by-slug/${params.category}/${params.slug}` : params.id;
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImg, setCurrentImg] = useState(0);
  const [message, setMessage] = useState('');
  const [msgSent, setMsgSent] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [claimSent, setClaimSent] = useState(false);
  const [claimMsg, setClaimMsg] = useState('');

  const { isLoaded } = useGoogleMaps();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/listings/${fetchKey}`).then(r => { setListing(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [fetchKey]);

  // SEO meta tags + JSON-LD Product schema
  const defaultTitle = listing ? (listing.title || `${listing.manufacturer} ${listing.model}`) : '';
  const seoTitle = listing?.seo_title || (listing ? `${defaultTitle} | ${listing.category?.replace('-', ' ')} in Vendita` : '');
  const seoDesc = listing?.seo_description || (listing ? `${defaultTitle} ${listing.variant || ''} (${listing.year || ''}) - ${listing.condition === 'new' ? 'Nuovo' : 'Usato'}, ${listing.total_time || 0}h, ${listing.location_city || ''}. ${listing.price ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(listing.price) : ''} su AeroDeals.` : '');
  const seoImage = listing?.images?.length > 0 ? `${API}/files/${listing.images[0]}` : null;
  useSEO({
    title: seoTitle,
    description: seoDesc,
    image: seoImage,
    type: 'product',
    keywords: listing?.seo_keywords,
    jsonLd: listing ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: defaultTitle,
      description: seoDesc,
      image: seoImage ? [seoImage] : undefined,
      brand: { '@type': 'Brand', name: listing.manufacturer || 'Aircraft' },
      category: (listing.category || '').replace('-', ' '),
      sku: listing.registration || listing.listing_id,
      offers: {
        '@type': 'Offer',
        price: listing.price || 0,
        priceCurrency: listing.currency || 'EUR',
        availability: 'https://schema.org/InStock',
        url: typeof window !== 'undefined' ? window.location.href : '',
        itemCondition: listing.condition === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
      },
    } : null,
  });

  useEffect(() => {
    if (!user || !listing) return;
    axios.get(`${API}/favorites/ids`, { withCredentials: true }).then(r => {
      setIsFav(r.data.includes(listing.listing_id));
    }).catch(() => {});
  }, [user, listing]);

  const toggleFav = async () => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/favorites/${listing.listing_id}`, {}, { withCredentials: true });
      setIsFav(res.data.favorited);
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return (
    <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center">
      <p className="text-zinc-500">Annuncio non trovato</p>
    </div>
  );

  const images = listing.images?.length > 0
    ? listing.images.map(p => `${API}/files/${p}`)
    : [PLACEHOLDER_IMAGES[listing.category] || PLACEHOLDER_IMAGES.default];

  const specs = [
    { label: 'Anno', value: listing.year, icon: Calendar },
    { label: 'Condizioni', value: listing.condition === 'new' ? 'Nuovo' : 'Usato' },
    { label: 'Ore Airframe', value: listing.total_time ? `${listing.total_time.toLocaleString()}h` : null, icon: Clock },
    { label: 'Atterraggi Totali', value: listing.total_landings || null },
    { label: 'Posti Totali', value: listing.seats || null, icon: Users },
    { label: 'Motore', value: listing.engine_type, icon: Fuel },
    { label: 'Ore Motore', value: listing.engine_hours ? `${listing.engine_hours.toLocaleString()}h` : null, icon: Clock },
    { label: 'Registrazione', value: listing.registration },
    { label: 'N. Serie', value: listing.serial_number },
    { label: 'Codice Aeroporto', value: listing.airport_code },
    { label: 'Ultraleggero Avanzato', value: listing.advanced_ultralight === 'si' ? 'Si' : listing.advanced_ultralight === 'no' ? 'No' : null },
    { label: 'Ultima Ispezione', value: listing.last_inspection_month && listing.last_inspection_year ? `${String(listing.last_inspection_month).padStart(2,'0')}/${listing.last_inspection_year}` : null },
    { label: 'Storico Danni', value: listing.damage_history === 'never' ? 'Mai danneggiato' : listing.damage_history === 'yes' ? 'Danneggiato' : null },
    { label: 'Tenuto in Hangar', value: listing.hangar_kept ? 'Si' : null },
    { label: 'Attrezzato IFR', value: listing.ifr_equipped ? 'Si' : null },
    { label: 'Certificato IFR', value: listing.ifr_certified ? 'Si' : null },
    { label: 'Uso Commerciale', value: listing.commercial_certified ? 'Si' : null },
    { label: 'JAR-OPS 1', value: listing.jar_ops_certified ? 'Si' : null },
    { label: 'Velocita Max', value: listing.max_speed ? `${listing.max_speed} ${listing.category === 'ultraleggeri' ? 'km/h' : 'kts'}` : null, icon: Gauge },
    { label: 'Autonomia', value: listing.range_km ? `${listing.range_km} nm` : null, icon: MapPin },
    { label: 'N. Motori', value: listing.num_engines > 1 ? listing.num_engines : null },
    { label: 'Ore Motore 2', value: listing.engine2_hours ? `${listing.engine2_hours.toLocaleString()}h` : null },
    { label: 'Elica', value: listing.propeller_type },
    { label: 'TBO Rimanenti', value: listing.tbo_remaining ? `${listing.tbo_remaining}h` : null },
    { label: 'Hot Section', value: listing.hot_section_completed ? 'Completata' : null },
    { label: 'Cicli Totali', value: listing.total_cycles ? listing.total_cycles.toLocaleString() : null },
    { label: 'Ore APU', value: listing.apu_hours ? `${listing.apu_hours}h` : null },
    { label: 'Config. Interni', value: listing.interior_config },
    { label: 'Pale Rotore', value: listing.rotor_blades || null },
    { label: 'Ore Rotore', value: listing.rotor_hours ? `${listing.rotor_hours}h` : null },
    { label: 'Ore Rotore di Coda', value: listing.tail_rotor_hours ? `${listing.tail_rotor_hours}h` : null },
    { label: 'MTOW', value: listing.mtow ? `${listing.mtow} kg` : null },
    { label: 'Carico Utile', value: listing.useful_load ? `${listing.useful_load} kg` : null },
    { label: 'Verricello', value: listing.hoist_equipped ? 'Si' : null },
    { label: 'EMS/HEMS', value: listing.ems_equipped ? 'Si' : null },
    { label: 'NVG', value: listing.nvg_compatible ? 'Si' : null },
    { label: 'Avionica', value: listing.avionics },
  ].filter(s => s.value);

  const sendMessage = async () => {
    if (!message.trim() || !user) return;
    try {
      await axios.post(`${API}/messages`, {
        listing_id: listing.listing_id,
        receiver_id: listing.user_id,
        content: message,
      }, { withCredentials: true });
      setMsgSent(true);
      setMessage('');
    } catch { /* ignore */ }
  };

  const hasCoords = listing.location_lat && listing.location_lng;

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="listing-detail-page">
      {/* Image Gallery */}
      <div className="relative w-full h-[50vh] md:h-[60vh] bg-zinc-950 overflow-hidden cursor-pointer" data-testid="listing-gallery" onClick={() => setLightboxOpen(true)}>
        <img src={images[currentImg]} alt={`${listing.title || (listing.manufacturer + ' ' + listing.model)} ${listing.year || ''} ${listing.location_city || ''} | AeroDeals`.trim()} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-4 right-4 flex gap-2">
          {user && (
            <button onClick={(e) => { e.stopPropagation(); toggleFav(); }} className={`bg-zinc-950/60 backdrop-blur-sm border border-zinc-700 px-3 py-1.5 flex items-center gap-2 text-xs hover:border-red-500/50 transition-colors ${isFav ? 'text-red-500' : 'text-white'}`} data-testid="fav-detail-btn">
              <Heart size={14} className={isFav ? 'fill-red-500' : ''} /> {isFav ? 'Salvato' : 'Salva'}
            </button>
          )}
          <div className="bg-zinc-950/60 backdrop-blur-sm border border-zinc-700 px-3 py-1.5 flex items-center gap-2 text-white text-xs pointer-events-none">
            <Maximize2 size={12} /> Clicca per ingrandire
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setCurrentImg((currentImg - 1 + images.length) % images.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-zinc-950/60 backdrop-blur-sm border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059] transition-colors" data-testid="gallery-prev">
              <ChevronLeft size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setCurrentImg((currentImg + 1) % images.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-zinc-950/60 backdrop-blur-sm border border-zinc-700 flex items-center justify-center text-white hover:border-[#C5A059] transition-colors" data-testid="gallery-next">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-auto">
              {images.map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentImg(i); }} className={`w-2 h-2 rounded-full transition-colors ${i === currentImg ? 'bg-[#C5A059]' : 'bg-zinc-600'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox images={images} startIndex={currentImg} onClose={() => setLightboxOpen(false)} title={listing.title || `${listing.manufacturer} ${listing.model}`} />
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[10px] font-mono uppercase tracking-wider bg-zinc-800 text-[#C5A059] px-2 py-1">{listing.category?.replace('-', ' ')}</span>
                <span className="text-xs text-zinc-600 flex items-center gap-1"><Eye size={12} /> {listing.views} visualizzazioni</span>
              </div>
              <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {listing.title || `${listing.manufacturer} ${listing.model} ${listing.variant || ''}`}
              </h1>
              <p className="text-3xl font-bold text-[#C5A059] mt-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="listing-price">
                {formatPrice(listing.price)}
              </p>
            </div>

            {/* Specs Grid */}
            <div data-testid="listing-specs">
              <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Scheda Tecnica</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {specs.map(s => (
                  <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-4">
                    <p className="text-xs text-zinc-500 mb-1">{s.label}</p>
                    <p className="text-sm font-medium text-white font-mono">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div>
                <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Descrizione</h2>
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap break-words">{listing.description}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">

              {/* Seller Profile */}
              <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="seller-profile-card">
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Venditore</h3>
                <div className="flex items-center gap-4">
                  {listing.user_picture ? (
                    <img
                      src={listing.user_picture.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${listing.user_picture}` : listing.user_picture}
                      alt={listing.user_name || 'Venditore'}
                      className="w-12 h-12 rounded-full object-cover border border-zinc-700 shrink-0 bg-zinc-800"
                      data-testid="seller-avatar"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 items-center justify-center shrink-0 ${listing.user_picture ? 'hidden' : 'flex'}`}
                    data-testid="seller-avatar-fallback"
                  >
                    <User size={20} className="text-zinc-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{listing.user_name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">Membro AeroDeals</p>
                  </div>
                </div>
              </div>

              {/* Messaging */}
              <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="contact-seller-card">
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-4">Messaggistica</h3>
                {user ? (
                  <div className="space-y-3">
                    {msgSent ? (
                      <p className="text-sm text-emerald-400" data-testid="message-sent-confirmation">Messaggio inviato!</p>
                    ) : (
                      <>
                        <Textarea
                          value={message}
                          onChange={e => setMessage(e.target.value)}
                          placeholder="Scrivi un messaggio al venditore..."
                          className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-600 min-h-[100px] text-sm"
                          data-testid="message-textarea"
                        />
                        <button onClick={sendMessage} className="w-full bg-[#C5A059] text-black py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors flex items-center justify-center gap-2" data-testid="send-message-btn">
                          <Send size={14} /> Invia Messaggio
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <Link to="/login" className="block text-center border border-zinc-700 py-2.5 text-sm text-zinc-300 hover:border-zinc-500 transition-colors" data-testid="login-to-message-link">
                    Accedi per inviare messaggi
                  </Link>
                )}
              </div>

              {/* Rivendica Annuncio - visible on admin-owned listings for everyone */}
              {listing.claimable && (!user || listing.user_id !== user.user_id) && (
                <div className="bg-zinc-900 border border-zinc-800 p-6" data-testid="claim-listing-card">
                  <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3 flex items-center gap-2">
                    <ShieldCheck size={14} /> Rivendica Annuncio
                  </h3>
                  <p className="text-xs text-zinc-400 mb-3 leading-relaxed">Sei il proprietario di questo velivolo? Richiedi il trasferimento dell'annuncio al tuo profilo.</p>
                  {!user ? (
                    <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`} className="w-full border border-[#C5A059] text-[#C5A059] py-2.5 text-sm font-medium hover:bg-[#C5A059]/10 transition-colors flex items-center justify-center gap-2" data-testid="claim-login-btn">
                      <ShieldCheck size={14} /> Accedi per Rivendicare
                    </Link>
                  ) : claimSent ? (
                    <p className="text-sm text-emerald-400" data-testid="claim-sent-msg">Richiesta inviata! Riceverai una notifica quando verrà approvata.</p>
                  ) : (
                    <div className="space-y-3">
                      <Textarea
                        value={claimMsg}
                        onChange={e => setClaimMsg(e.target.value)}
                        placeholder="Descrivi perché sei il proprietario (es. matricola, documenti)..."
                        className="bg-zinc-800 border-zinc-700 text-white placeholder-zinc-600 min-h-[80px] text-sm"
                        data-testid="claim-message-textarea"
                      />
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/claims`, { listing_id: listing.listing_id, message: claimMsg }, { withCredentials: true });
                            setClaimSent(true);
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Errore nell\'invio della richiesta');
                          }
                        }}
                        className="w-full border border-[#C5A059] text-[#C5A059] py-2.5 text-sm font-medium hover:bg-[#C5A059]/10 transition-colors flex items-center justify-center gap-2"
                        data-testid="claim-submit-btn"
                      >
                        <ShieldCheck size={14} /> Invia Richiesta
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Map */}
              {hasCoords && isLoaded && (
                <div className="bg-zinc-900 border border-zinc-800 overflow-hidden" data-testid="listing-map">
                  <div className="h-[240px]">
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={{ lat: listing.location_lat, lng: listing.location_lng }}
                      zoom={10}
                      options={{ disableDefaultUI: true, zoomControl: true }}
                    >
                      <Marker position={{ lat: listing.location_lat, lng: listing.location_lng }} />
                    </GoogleMap>
                  </div>
                  <div className="p-3 flex items-center gap-2 border-t border-zinc-800">
                    <MapPin size={14} className="text-[#C5A059]" />
                    <span className="text-xs text-zinc-400">{listing.location_city}{listing.airport_code ? ` (${listing.airport_code})` : ''}</span>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Similar */}
        {listing.similar?.length > 0 && (
          <div className="mt-16" data-testid="similar-listings">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-6">Annunci Simili</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {listing.similar.map(s => <ListingCard key={s.listing_id} listing={s} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
